import { z } from 'zod';
import type { StoredSession } from '../app/storedSession';
import {
  DomainError,
  snapshotSchema,
  type Mutation,
  type Snapshot,
} from '../domain/model';

const resultSchema = z.object({
  status: z.enum(['applied', 'duplicate']),
  data: z.record(z.string(), z.unknown()),
  snapshot: snapshotSchema.optional(),
});
const errorSchema = z.object({
  status: z.enum(['conflict', 'invalid', 'unauthorized', 'temporary']),
  message: z.string(),
});
export type Session = StoredSession & {
  generation: number;
  controller: AbortController;
};
export type MutationResponse = z.infer<typeof resultSchema>;
export async function establishSession(uid: string, token: string): Promise<StoredSession> {
  const response = await fetch('/api/session', {
    method: 'POST', credentials: 'same-origin',
    headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new DomainError(response.status === 401 ? 'unauthorized' : 'temporary', '無法保存登入狀態，請稍後再試');
  const metadata = z.object({ uid: z.string(), expiresAt: z.number() }).parse(await response.json());
  if (metadata.uid !== uid) throw new DomainError('unauthorized', '登入帳號與資料不符');
  return { ...metadata, token: '', persistent: true };
}

export async function endSession(uid: string): Promise<void> {
  const response = await fetch('/api/session', {
    method: 'DELETE', credentials: 'same-origin', headers: { 'X-LexisPulse-User': uid }, signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error('登出暫時無法完成，請稍後再試');
}
export async function callApi(
  session: Session,
  body: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  if (!session.token && !session.persistent) throw new DomainError('unauthorized', '請重新登入原帳號以同步');
  let response: Response;
  try {
    response = await fetch('/api/v2', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-LexisPulse-User': session.uid,
        ...(session.token ? { Authorization: `Bearer ${session.token}` } : {}) },
      body: JSON.stringify(body),
      signal: AbortSignal.any([
        session.controller.signal,
        AbortSignal.timeout(55000),
        ...(signal ? [signal] : []),
      ]),
    });
  } catch (error) {
    if (session.controller.signal.aborted || signal?.aborted) throw error;
    throw new DomainError('temporary', '無法連線，紀錄已保留');
  }
  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    if (session.controller.signal.aborted || signal?.aborted) throw error;
    throw new DomainError('temporary', '服務回應格式不完整，請稍後重試');
  }
  const problem = errorSchema.safeParse(data);
  if (problem.success) throw new DomainError(problem.data.status, problem.data.message);
  if (!response.ok) throw new DomainError('temporary', '服務暫時無法處理請求');
  return data;
}
export async function fetchSnapshot(session: Session, signal?: AbortSignal): Promise<Snapshot> {
  const response = (await callApi(
    session,
    { schemaVersion: 2, operation: 'snapshot' },
    signal,
  )) as { data?: unknown };
  const data = snapshotSchema.parse(response.data);
  if (data.uid !== session.uid) throw new DomainError('unauthorized', '登入帳號與資料不符');
  return data;
}
export async function sendMutation(session: Session, mutation: Mutation): Promise<MutationResponse> {
  return resultSchema.parse(await callApi(session, mutation));
}
